import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ChevronRight,
    CornerDownLeft,
    Folder,
    FolderPlus,
    FolderTree,
    LayoutGrid,
    Loader2,
    Package,
    Pencil,
    Plus,
    Search,
    Sparkles,
    Trash2,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { PageContainer } from '../../../common/components/PageContainer';
import { useCategoriesApi, CategoryNode, CreateCategoryPayload } from '../../../common/hooks/use-categories-api.hook';
import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { signalRunStarted, useAgentWorkSignal, useProfileRunning } from '../../../modules/vendor/work/agent-work.store';
import { useProductsApi } from '../../../common/hooks/use-products-api.hook';
import { useVendorFamilies } from '../../../common/hooks/use-vendor-families.hook';
import { ProductFamily } from '../../../common/models/product-family.model';
import { Toast } from '../../../components/design-system/common/Toast';
import { Product } from '../../../modules/vendor/my-products/models/product.model';
import { config as vendorConfig } from '../../../common/hooks/use-settings-api.hook';

// ============================================================================
// Category Tree Item Component (with drop target)
// ============================================================================

const ALL_PRODUCTS_ID = '__all__';

interface TreeItemProps {
    node: CategoryNode;
    selectedId: string | null;
    onSelect: (node: CategoryNode) => void;
    onAddChild: (parentId: string) => void;
    onDelete: (node: CategoryNode) => void;
    onDropProduct: (categoryId: string, productUuids: string[], sourceCategoryId?: string | null) => void;
    depth?: number;
}

const CategoryTreeItem = ({ node, selectedId, onSelect, onAddChild, onDelete, onDropProduct, depth = 0 }: TreeItemProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);
    const [dragOver, setDragOver] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedId === node._id;
    const displayName = typeof node.name === 'string' ? node.name : (node.name?.en || Object.values(node.name)[0] || '');

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setDragOver(true);
    };

    const handleDragLeave = () => setDragOver(false);

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        try {
            const data = JSON.parse(e.dataTransfer.getData('application/json'));
            if (data?.productUuids?.length) {
                onDropProduct(node._id, data.productUuids, data.sourceCategoryId);
            }
        } catch {}
    };

    return (
        <div>
            <div
                onClick={() => onSelect(node)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                // Depth is data, not a design decision, so the indent is computed rather than classed.
                style={{ marginInlineStart: depth * 16 }}
                className={cn(
                    'flex cursor-pointer items-center gap-1.5 px-2.5 py-1.5 transition-colors',
                    isSelected ? 'bg-muted' : 'hover:bg-muted/60',
                    dragOver && 'bg-muted outline outline-2 outline-dashed -outline-offset-2 outline-border',
                )}
            >
                {confirmingDelete ? (
                    /* Inline delete confirmation — matches normal row layout */
                    <>
                        <div className="w-5 shrink-0" /> {/* spacer matching chevron */}
                        <p className="flex-1 truncate text-sm text-muted-foreground">
                            {t('CATEGORIES.CONFIRM_DELETE_INLINE', { name: displayName, defaultValue: `Delete "${displayName}"?` })}
                        </p>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="xs"
                                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(false); }}
                            >
                                {t('COMMON.CANCEL', 'Cancel')}
                            </Button>
                            <Button
                                variant="ghost"
                                size="xs"
                                className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(false); onDelete(node); }}
                            >
                                {t('COMMON.DELETE', 'Delete')}
                            </Button>
                        </div>
                    </>
                ) : (
                    /* Normal row content */
                    <>
                        <span
                            onClick={(e) => { e.stopPropagation(); if (hasChildren) setExpanded(!expanded); }}
                            className={cn(
                                'flex size-5 shrink-0 items-center justify-center',
                                hasChildren ? 'cursor-pointer hover:bg-muted' : 'cursor-default opacity-0',
                            )}
                        >
                            <ChevronRight className={cn('size-3.5 text-muted-foreground transition-transform', expanded && 'rotate-90')} />
                        </span>

                        <div className="flex min-w-0 flex-1 items-center gap-1.5">
                            <Folder className={cn('size-4 shrink-0', isSelected ? 'text-foreground' : 'text-muted-foreground')} />
                            <span className={cn('flex-1 truncate text-sm text-foreground', isSelected && 'font-medium')}>
                                {displayName}
                            </span>
                        </div>

                        {node.productCount > 0 && (
                            <span className="min-w-5 bg-muted px-1.5 text-center text-xs font-medium leading-4 text-muted-foreground">
                                {node.productCount}
                            </span>
                        )}

                        <div className="flex items-center gap-0.5">
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                onClick={(e) => { e.stopPropagation(); onAddChild(node._id); }}
                                title="Add subcategory"
                            >
                                <Plus className="text-muted-foreground" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                className="hover:bg-destructive/5 hover:text-destructive"
                                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
                                title="Delete"
                            >
                                <Trash2 className="text-muted-foreground" />
                            </Button>
                        </div>
                    </>
                )}
            </div>

            {hasChildren && (
                <Collapsible open={expanded}>
                    <CollapsibleContent>
                        {node.children.map(child => (
                            <CategoryTreeItem
                                key={child._id}
                                node={child}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                onAddChild={onAddChild}
                                onDelete={onDelete}
                                onDropProduct={onDropProduct}
                                depth={depth + 1}
                            />
                        ))}
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
};

// ============================================================================
// Draggable Product Row
// ============================================================================

interface DraggableProductCardProps {
    product: Product;
    onRemove?: () => void;
    showRemove?: boolean;
    sourceCategoryId?: string;
    isSelected?: boolean;
    onToggleSelect?: () => void;
    allSelectedUuids?: Set<string>;
}

const DraggableProductCard = ({ product, onRemove, showRemove = true, sourceCategoryId, isSelected, onToggleSelect, allSelectedUuids }: DraggableProductCardProps) => {
    const { t } = useTranslation();
    const handleDragStart = (e: React.DragEvent) => {
        const uuids = (isSelected && allSelectedUuids && allSelectedUuids.size > 1)
            ? Array.from(allSelectedUuids)
            : [product.uuid];
        e.dataTransfer.setData('application/json', JSON.stringify({
            productUuids: uuids,
            sourceCategoryId: sourceCategoryId || null,
        }));
        e.dataTransfer.effectAllowed = 'copyMove';
    };

    return (
        <div
            draggable
            onDragStart={handleDragStart}
            className={cn(
                'group relative cursor-grab overflow-hidden transition-colors active:cursor-grabbing',
                isSelected ? 'border-2 border-foreground' : 'border border-border hover:border-muted-foreground',
            )}
        >
            {/* Thumbnail */}
            <div className="relative">
                <img
                    src={product.thumbnailPath || ''}
                    alt=""
                    className="block aspect-square w-full bg-muted object-cover"
                    onError={(e: any) => { e.target.src = ''; }}
                />
                {(product as any).variantCount > 0 && (
                    <span className="absolute bottom-1 end-1 bg-foreground/70 px-1 text-[9px] font-medium leading-tight text-background">
                        +{(product as any).variantCount}
                    </span>
                )}
            </div>
            {/* Name overlay */}
            <div className="border-t border-border bg-card rounded-xl px-1.5 py-1">
                <p className="truncate text-xs font-medium leading-tight text-foreground">{product.name}</p>
                {product.sku && (
                    <p className="truncate text-[10px] leading-tight text-muted-foreground">{product.sku}</p>
                )}
            </div>
            {/* Checkbox */}
            {onToggleSelect && (
                <div className="absolute start-1 top-1" onClick={e => e.stopPropagation()}>
                    <Checkbox
                        checked={!!isSelected}
                        onCheckedChange={() => onToggleSelect()}
                        className="bg-background/70"
                    />
                </div>
            )}
            {/* Remove button */}
            {showRemove && onRemove && (
                <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t('CATEGORIES.REMOVE_PRODUCT', 'Remove product from category') as string}
                    onClick={e => { e.stopPropagation(); onRemove(); }}
                    className="absolute end-1 top-1 bg-background/85 text-destructive opacity-0 transition-opacity group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                >
                    <X />
                </Button>
            )}
        </div>
    );
};

// ============================================================================
// Product Picker Dialog
// ============================================================================

interface ProductPickerProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (uuids: string[]) => void;
    /** Limit the picker to one family's products. */
    familySlug?: string;
    existingUuids: string[];
}

/** How many products the picker shows at once — search narrows the rest. */
const PICKER_PAGE_SIZE = 50;

const ProductPickerDialog = ({ open, onClose, onConfirm, familySlug, existingUuids }: ProductPickerProps) => {
    const { t } = useTranslation();
    const productsApi = useProductsApi();
    const [search, setSearch] = useState('');
    // Searched on Enter, on the server: the picker never holds the whole catalogue.
    const [submitted, setSubmitted] = useState('');
    const [results, setResults] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        productsApi
            .findProductsPage({ page: 1, limit: PICKER_PAGE_SIZE, draft: 'all', familySlug, search: submitted || undefined })
            .then(({ products }) => { if (!cancelled) setResults(products); })
            .catch(() => { if (!cancelled) setResults([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, submitted, familySlug]);

    const filteredProducts = useMemo(
        () => results.filter(p => !existingUuids.includes(p.uuid)),
        [results, existingUuids],
    );

    const toggleProduct = (uuid: string) => {
        setSelected(prev => prev.includes(uuid) ? prev.filter(u => u !== uuid) : [...prev, uuid]);
    };

    const handleConfirm = () => {
        onConfirm(selected);
        setSelected([]);
        setSearch('');
        setSubmitted('');
    };

    return (
        <Dialog open={open} onOpenChange={next => { if (!next) onClose(); }}>
            <DialogContent className="gap-0 p-0 sm:max-w-lg">
                <DialogHeader className="border-b border-border px-5 py-4">
                    <DialogTitle>{t('CATEGORIES.ADD_PRODUCTS', 'Add Products')}</DialogTitle>
                </DialogHeader>

                <div className="px-5 pb-2 pt-4">
                    <form
                        className="relative"
                        onSubmit={e => { e.preventDefault(); setSubmitted(search.trim()); }}
                    >
                        <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            className="h-9 ps-8 pe-20"
                            placeholder={t('CATEGORIES.SEARCH_PRODUCTS', 'Search by name or SKU...')}
                            value={search}
                            onChange={e => {
                                setSearch(e.target.value);
                                // Emptying the box shows everything again without an extra Enter.
                                if (!e.target.value.trim()) setSubmitted('');
                            }}
                        />
                        <div className="absolute end-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
                            {loading && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
                            {!loading && search.trim() && search.trim() !== submitted && (
                                <button
                                    type="submit"
                                    aria-label={t('PAGES.MY_PRODUCTS.SEARCH_PRESS_ENTER') as string}
                                    title={t('PAGES.MY_PRODUCTS.SEARCH_PRESS_ENTER') as string}
                                    className="flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground hover:text-foreground"
                                >
                                    <CornerDownLeft className="size-3" />
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {selected.length > 0 && (
                    <div className="mx-5 mt-2 flex items-center gap-2 bg-muted px-3 py-1.5">
                        <span className="text-xs font-medium text-foreground">{selected.length} selected</span>
                        <button
                            type="button"
                            onClick={() => setSelected([])}
                            className="ms-auto text-xs text-muted-foreground hover:text-foreground hover:underline"
                        >
                            Clear
                        </button>
                    </div>
                )}

                <div className="flex max-h-[380px] flex-col gap-0.5 overflow-auto px-5 py-3">
                    {filteredProducts.slice(0, 100).map(product => {
                        const isChecked = selected.includes(product.uuid);
                        return (
                            <div
                                key={product.uuid}
                                onClick={() => toggleProduct(product.uuid)}
                                className={cn(
                                    'flex cursor-pointer items-center gap-3 px-2.5 py-1.5 transition-colors',
                                    isChecked ? 'bg-muted' : 'hover:bg-muted/60',
                                )}
                            >
                                <Checkbox checked={isChecked} className="pointer-events-none" tabIndex={-1} />
                                <img
                                    src={product.thumbnailPath || ''}
                                    alt=""
                                    className="size-9 shrink-0 bg-muted object-cover"
                                    onError={(e: any) => { e.target.style.display = 'none'; }}
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-foreground">{product.name}</p>
                                    {product.sku && <p className="truncate text-xs text-muted-foreground">{product.sku}</p>}
                                </div>
                            </div>
                        );
                    })}
                    {!loading && filteredProducts.length === 0 && (
                        <p className="py-10 text-center text-sm text-muted-foreground">
                            {t('CATEGORIES.NO_PRODUCTS_FOUND', 'No products found')}
                        </p>
                    )}
                </div>

                {/*
                  `mx-0 mb-0` undoes DialogFooter's own `-mx-4 -mb-4`.
                  
                  Those negative margins exist to pull the footer out to the
                  edges of a DialogContent that has the default `p-4`. Every
                  dialog on this page sets `p-0` and pads its own sections, so
                  the footer was being dragged 16px PAST the dialog on three
                  sides — which is the white bar that broke the rounded corners
                  along the bottom.
                */}
                <DialogFooter className="mx-0 mb-0 border-t border-border px-5 py-4">
                    <Button variant="outline" onClick={onClose}>{t('COMMON.CANCEL', 'Cancel')}</Button>
                    <Button onClick={handleConfirm} disabled={selected.length === 0}>
                        {t('CATEGORIES.ASSIGN', 'Add')} {selected.length > 0 ? `(${selected.length})` : ''}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const resolveName = (name: any, lang = 'en') => typeof name === 'string' ? name : (name?.[lang] || Object.values(name || {})[0] || '');

// ============================================================================
// Droppable Section (wraps header + children as a single drop zone)
// ============================================================================

const DroppableSection = ({ label, count, categoryId, onDropProduct, children }: {
    label: string; count: number; categoryId: string;
    onDropProduct: (categoryId: string, productUuids: string[], sourceCategoryId?: string | null) => void;
    children: React.ReactNode;
}) => {
    const [dragOver, setDragOver] = useState(false);
    // Nested children fire dragleave on the parent, so the depth is counted rather than trusted.
    const dragCounter = useRef(0);
    return (
        <div
            onDragEnter={e => { e.preventDefault(); dragCounter.current++; setDragOver(true); }}
            onDragOver={e => e.preventDefault()}
            onDragLeave={() => { dragCounter.current--; if (dragCounter.current <= 0) { dragCounter.current = 0; setDragOver(false); } }}
            onDrop={e => {
                e.preventDefault();
                dragCounter.current = 0;
                setDragOver(false);
                try {
                    const data = JSON.parse(e.dataTransfer.getData('application/json'));
                    if (data?.productUuids?.length) onDropProduct(categoryId, data.productUuids, data.sourceCategoryId);
                } catch {}
            }}
            className={cn(
                'border border-dashed transition-colors',
                dragOver ? 'border-foreground bg-muted' : 'border-transparent',
            )}
        >
            <div className="flex items-center gap-1.5 px-2.5 py-1.5">
                <Folder className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {label}
                </span>
                <span className="text-xs text-muted-foreground">({count})</span>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(90px,1fr))] gap-2 px-1 pb-1">
                {children}
            </div>
        </div>
    );
};

// ============================================================================
// Bulk Assign Tree (flat clickable list for picking a target category)
// ============================================================================

const BulkAssignTreeNode = ({ node, onSelect, depth = 0 }: { node: CategoryNode; onSelect: (id: string) => void; depth?: number }) => {
    const displayName = typeof node.name === 'string' ? node.name : (node.name?.en || Object.values(node.name)[0] || '');
    return (
        <>
            <div
                onClick={() => onSelect(node._id)}
                style={{ marginInlineStart: depth * 16 }}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 transition-colors hover:bg-muted"
            >
                <Folder className="size-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-sm text-foreground">{displayName}</span>
                <span className="text-xs text-muted-foreground">{node.productCount}</span>
            </div>
            {node.children?.map(child => (
                <BulkAssignTreeNode key={child._id} node={child} onSelect={onSelect} depth={depth + 1} />
            ))}
        </>
    );
};

const BulkAssignTree = ({ nodes, onSelect }: { nodes: CategoryNode[]; onSelect: (id: string) => void }) => (
    <div>
        {nodes.map(node => (
            <BulkAssignTreeNode key={node._id} node={node} onSelect={onSelect} />
        ))}
    </div>
);

// ============================================================================
// Main Categories Page
// ============================================================================

const CategoriesPage = () => {
    const { t } = useTranslation();
    const categoriesApi = useCategoriesApi();
    const contentStudioApi = useContentStudioApi();
    /* Any agent run finishing reloads the tree. The categoriser writes
       categories directly, so the page is stale the moment it ends —
       including when it was started here and finished after the vendor
       had wandered off and come back. */
    const agentFinishedAt = useAgentWorkSignal((state) => state.finishedAt);
    /* Spins for as long as the work actually runs, not just while the
       request to start it is in flight. Read from the store, so this page
       does not poll a second time to learn what the toaster already knows. */
    const categorising = useProfileRunning('categoriser');
    const productsApi = useProductsApi();
    const { families, loading: familiesLoading } = useVendorFamilies();
    const [activeFamilySlug, setActiveFamilySlug] = useState<string>('');

    const [tree, setTree] = useState<CategoryNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<CategoryNode | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [createParentId, setCreateParentId] = useState<string | undefined>();
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [deleteConfirmNode, setDeleteConfirmNode] = useState<CategoryNode | null>(null);
    const [showProductPicker, setShowProductPicker] = useState(false);
    const [categoryProducts, setCategoryProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    // "All Products" pagination state
    const [allProductsPage, setAllProductsPage] = useState(1);
    const [allProductsData, setAllProductsData] = useState<Product[]>([]);
    const [allProductsNextPage, setAllProductsNextPage] = useState<number>(-1);
    const [allProductsLoading, setAllProductsLoading] = useState(false);

    // Toast state
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const showToast = (message: string, type: 'success' | 'error' = 'success') => setToast({ message, type });

    // Move-or-Add dialog state
    const [moveOrAddDialog, setMoveOrAddDialog] = useState<{
        productUuids: string[];
        sourceCategoryId: string;
        targetCategoryId: string;
        targetName: string;
    } | null>(null);

    // Bulk selection state
    const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
    const [showBulkAssignPicker, setShowBulkAssignPicker] = useState(false);
    const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);

    // AI Agent state
    const [showAiConfirm, setShowAiConfirm] = useState(false);
    const [aiMode, setAiMode] = useState<'generate' | 'assign'>('generate');
    /* Only the moment between pressing the button and the run existing. The
       work itself is watched from the dashboard, not from here. */
    const [aiStarting, setAiStarting] = useState(false);
    const [leftPanelWidth, setLeftPanelWidth] = useState(380);

    // Category cover state
    const [coverDialogOpen, setCoverDialogOpen] = useState(false);
    const [coverBusy, setCoverBusy] = useState(false);
    const coverFileInputRef = useRef<HTMLInputElement | null>(null);
    const resizingRef = useRef(false);

    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        resizingRef.current = true;
        const startX = e.clientX;
        const startWidth = leftPanelWidth;
        const onMove = (ev: MouseEvent) => {
            if (!resizingRef.current) return;
            const newWidth = Math.max(280, Math.min(600, startWidth + ev.clientX - startX));
            setLeftPanelWidth(newWidth);
        };
        const onUp = () => {
            resizingRef.current = false;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [leftPanelWidth]);

    // Clear selection when switching categories
    useEffect(() => { setSelectedUuids(new Set()); }, [selectedCategory?._id]);

    // Auto-scroll AI log

    const toggleSelectUuid = (uuid: string) => {
        setSelectedUuids(prev => {
            const next = new Set(prev);
            if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const products = isAllProductsSelected ? allProductsData : categoryProducts;
        if (selectedUuids.size === products.length) {
            setSelectedUuids(new Set());
        } else {
            setSelectedUuids(new Set(products.map((p: any) => p.uuid)));
        }
    };

    const handleBulkAssign = async (targetCategoryId: string) => {
        if (selectedUuids.size === 0) return;
        const uuids = Array.from(selectedUuids);
        setShowBulkAssignPicker(false);
        try {
            await categoriesApi.assignProducts(targetCategoryId, uuids);
            await loadTree();
            if (selectedCategory && selectedCategory._id !== ALL_PRODUCTS_ID) {
                await refreshCategoryProducts(selectedCategory._id);
            }
            const targetNode = findNodeById(tree, targetCategoryId);
            const targetName = targetNode ? (typeof targetNode.name === 'string' ? targetNode.name : (targetNode.name?.en || '')) : '';
            setSelectedUuids(new Set());
            showToast(t('CATEGORIES.TOAST_BULK_ASSIGNED', '{{count}} product(s) assigned to "{{name}}"', { count: uuids.length, name: targetName }));
        } catch (err) {
            console.error('Failed to bulk assign:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    const handleAiCategorize = () => {
        if (!activeFamilySlug) return;
        setAiMode(tree.length > 0 ? 'assign' : 'generate');
        setShowAiConfirm(true);
    };

    /**
     * Hand the job to the agent and let go of it.
     *
     * It used to stream over SSE into a panel on this page, which tied the work
     * to the tab: navigating away lost the progress and left no way back to it.
     * Now it is a durable run like every other agent job — it shows under Agents
     * at work on the dashboard, survives leaving the page, and is billed against
     * AI credits.
     *
     * `approved` autonomy because creating categories and assigning products are
     * writes, and at `suggest` they would only ever be proposed. The vendor
     * pressed a button that says Generate with AI having chosen the mode; that
     * is the approval.
     */
    const handleAiRun = async () => {
        if (!activeFamilySlug) return;
        setShowAiConfirm(false);
        setAiStarting(true);
        try {
            await contentStudioApi.createAgentRun({
                profile: 'categoriser',
                autonomy: 'approved',
                message:
                    aiMode === 'assign'
                        ? (t('CATEGORIES.AI_TASK_ASSIGN') as string)
                        : (t('CATEGORIES.AI_TASK_GENERATE') as string),
                context: {
                    familySlug: activeFamilySlug,
                    language: vendorConfig?.language?.[0] || 'en',
                    mode: aiMode,
                },
            });
            // The dashboard and Work watch this signal, so the run appears
            // wherever the vendor happens to go next.
            signalRunStarted();
            showToast(t('CATEGORIES.AI_STARTED', 'Working on it — you can leave this page'), 'success');
        } catch (err) {
            console.error('Failed to start categorisation run:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        } finally {
            setAiStarting(false);
        }
    };

    const handleRemoveAll = async () => {
        if (!activeFamilySlug) return;
        setShowRemoveAllConfirm(false);
        try {
            const result = await categoriesApi.removeAll(activeFamilySlug);
            showToast(t('CATEGORIES.TOAST_REMOVED_ALL', `Deleted ${result.deleted} categories`), 'success');
            setSelectedCategory(null);
            setCategoryProducts([]);
            loadTree();
        } catch (err) {
            console.error('Failed to remove all categories:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    const isAllProductsSelected = selectedCategory?._id === ALL_PRODUCTS_ID;
    const allProductsNode: CategoryNode = { _id: ALL_PRODUCTS_ID, name: t('CATEGORIES.ALL_PRODUCTS', 'All Products'), slug: 'all', productCount: 0, children: [], order: 0, depth: 0 };

    // Set default family slug when families load
    useEffect(() => {
        if (families.length > 0 && !activeFamilySlug) {
            const defaultFamily = families.find(f => (f as any).isDefault) || families[0];
            if (defaultFamily) setActiveFamilySlug(defaultFamily.slug);
        }
    }, [families]);

    const loadTree = useCallback(async (familySlug?: string) => {
        const slug = familySlug || activeFamilySlug || undefined;
        try {
            setLoading(true);
            console.log('[CategoriesPage] loadTree called with familySlug:', slug);
            const data = await categoriesApi.getTree(slug);
            console.log('[CategoriesPage] loadTree result:', data?.length, 'categories');
            setTree(data || []);
        } catch (err) {
            console.error('Failed to load categories:', err);
            setTree([]);
        } finally {
            setLoading(false);
        }
    }, [activeFamilySlug]);

    // Reload tree when active family changes
    useEffect(() => {
        if (activeFamilySlug) {
            setSelectedCategory(null);
            loadTree(activeFamilySlug).catch(() => {});
        }
    }, [activeFamilySlug]);

    /*
     * And when an agent run finishes.
     *
     * The categoriser writes categories straight into the tree, so this page is
     * stale the instant it ends — whether the vendor watched it or started it,
     * left, and came back. The signal fires for any run; reloading a tree is
     * cheap enough not to bother filtering by profile.
     */
    useEffect(() => {
        if (agentFinishedAt) loadTree().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentFinishedAt]);

    const findNodeById = useCallback((nodes: CategoryNode[], id: string): CategoryNode | null => {
        for (const node of nodes) {
            if (node._id === id) return node;
            const found = findNodeById(node.children || [], id);
            if (found) return found;
        }
        return null;
    }, []);

    useEffect(() => {
        if (selectedCategory && selectedCategory._id !== ALL_PRODUCTS_ID && tree.length > 0) {
            const fresh = findNodeById(tree, selectedCategory._id);
            if (fresh) setSelectedCategory(fresh);
        }
    }, [tree]);

    // Load products for selected category
    useEffect(() => {
        if (!selectedCategory) {
            setCategoryProducts([]);
            return;
        }
        if (selectedCategory._id === ALL_PRODUCTS_ID) {
            // Reset pagination and load first page
            setAllProductsData([]);
            setAllProductsPage(1);
            setCategoryProducts([]);
            return;
        }
        let cancelled = false;
        const fetchProducts = async () => {
            setLoadingProducts(true);
            try {
                const result = await categoriesApi.getProducts(selectedCategory._id);
                if (!cancelled) setCategoryProducts(result.data);
            } catch (err) {
                console.error('Failed to load category products:', err);
                if (!cancelled) setCategoryProducts([]);
            } finally {
                if (!cancelled) setLoadingProducts(false);
            }
        };
        fetchProducts();
        return () => { cancelled = true; };
    }, [selectedCategory?._id]);

    // "All Products" pagination loader
    useEffect(() => {
        if (!isAllProductsSelected) return;
        let cancelled = false;
        const loadPage = async () => {
            setAllProductsLoading(true);
            try {
                const result = await categoriesApi.searchProducts({ page: allProductsPage, limit: 30, familySlug: activeFamilySlug || undefined });
                if (cancelled) return;
                setAllProductsData(prev => allProductsPage === 1 ? result.data : [...prev, ...result.data]);
                setAllProductsNextPage(result.nextPage);
            } catch (err) {
                console.error('Failed to load products:', err);
            } finally {
                if (!cancelled) setAllProductsLoading(false);
            }
        };
        loadPage();
        return () => { cancelled = true; };
    }, [isAllProductsSelected, allProductsPage]);

    const handleCreate = async () => {
        if (!newCategoryName.trim()) return;
        try {
            await categoriesApi.create({ name: newCategoryName.trim(), parentId: createParentId, familySlug: activeFamilySlug } as CreateCategoryPayload);
            setNewCategoryName('');
            setIsCreating(false);
            setCreateParentId(undefined);
            await loadTree();
            showToast(t('CATEGORIES.TOAST_CREATED', 'Category created successfully'));
        } catch (err) {
            console.error('Failed to create category:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    const handleUpdate = async () => {
        if (!selectedCategory || !editName.trim()) return;
        try {
            await categoriesApi.update(selectedCategory._id, { name: editName.trim() });
            setIsEditing(false);
            await loadTree();
            showToast(t('CATEGORIES.TOAST_UPDATED', 'Category updated'));
        } catch (err) {
            console.error('Failed to update category:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    const handleGenerateCover = async () => {
        if (!selectedCategory || isAllProductsSelected) return;
        setCoverBusy(true);
        try {
            const res = await categoriesApi.generateCover(selectedCategory._id);
            await loadTree();
            showToast(res.image
                ? t('CATEGORIES.COVER_GENERATED', 'Cover generated')
                : t('CATEGORIES.COVER_NO_PRODUCTS', 'No products with images to generate a cover'), res.image ? 'success' : 'error');
        } catch (err) {
            console.error('Failed to generate cover:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        } finally {
            setCoverBusy(false);
        }
    };

    const handleUploadCover = async (file?: File | null) => {
        if (!selectedCategory || isAllProductsSelected || !file) return;
        setCoverBusy(true);
        try {
            await categoriesApi.uploadCover(selectedCategory._id, file);
            await loadTree();
            showToast(t('CATEGORIES.COVER_UPLOADED', 'Cover updated'));
        } catch (err) {
            console.error('Failed to upload cover:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        } finally {
            setCoverBusy(false);
            if (coverFileInputRef.current) coverFileInputRef.current.value = '';
        }
    };

    const handleRemoveCover = async () => {
        if (!selectedCategory || isAllProductsSelected) return;
        setCoverBusy(true);
        try {
            await categoriesApi.removeCover(selectedCategory._id);
            await loadTree();
            showToast(t('CATEGORIES.COVER_REMOVED', 'Cover removed'));
        } catch (err) {
            console.error('Failed to remove cover:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        } finally {
            setCoverBusy(false);
        }
    };

    const handleDirectDelete = async (node: CategoryNode) => {
        try {
            await categoriesApi.remove(node._id);
            if (selectedCategory?._id === node._id) setSelectedCategory(null);
            await loadTree();
            showToast(t('CATEGORIES.TOAST_DELETED', 'Category deleted'));
        } catch (err) {
            console.error('Failed to delete category:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    const refreshCategoryProducts = useCallback(async (categoryId: string) => {
        try {
            const result = await categoriesApi.getProducts(categoryId);
            setCategoryProducts(result.data);
        } catch (err) {
            console.error('Failed to refresh category products:', err);
        }
    }, []);

    const handleAssignProducts = async (productUuids: string[]) => {
        if (!selectedCategory || productUuids.length === 0) return;
        try {
            await categoriesApi.assignProducts(selectedCategory._id, productUuids);
            setShowProductPicker(false);
            await loadTree();
            await refreshCategoryProducts(selectedCategory._id);
            showToast(t('CATEGORIES.TOAST_PRODUCTS_ADDED', '{{count}} product(s) added to category', { count: productUuids.length }));
        } catch (err) {
            console.error('Failed to assign products:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    const handleRemoveProduct = async (uuid: string) => {
        if (!selectedCategory) return;
        try {
            await categoriesApi.removeProducts(selectedCategory._id, [uuid]);
            await loadTree();
            await refreshCategoryProducts(selectedCategory._id);
            showToast(t('CATEGORIES.TOAST_PRODUCT_REMOVED', 'Product removed from category'));
        } catch (err) {
            console.error('Failed to remove product:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    /** Drop handler: assign dragged products to a category */
    const handleDropProduct = useCallback(async (categoryId: string, productUuids: string[], sourceCategoryId?: string | null) => {
        const targetNode = findNodeById(tree, categoryId);
        const targetName = targetNode ? (typeof targetNode.name === 'string' ? targetNode.name : (targetNode.name?.en || '')) : '';

        // If product is being dragged from a different category, ask move or add
        if (sourceCategoryId && sourceCategoryId !== categoryId && sourceCategoryId !== ALL_PRODUCTS_ID) {
            setMoveOrAddDialog({ productUuids, sourceCategoryId, targetCategoryId: categoryId, targetName });
            return;
        }

        // Direct assign (from "All Products" or no source)
        try {
            await categoriesApi.assignProducts(categoryId, productUuids);
            await loadTree();
            if (selectedCategory && selectedCategory._id === categoryId) {
                await refreshCategoryProducts(categoryId);
            }
            showToast(t('CATEGORIES.TOAST_PRODUCT_MOVED', 'Product added to "{{name}}"', { name: targetName }));
        } catch (err) {
            console.error('Failed to assign via drag:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    }, [tree, selectedCategory]);

    /** Handle the move-or-add dialog actions */
    const handleMoveOrAddConfirm = async (action: 'move' | 'add') => {
        if (!moveOrAddDialog) return;
        const { productUuids, sourceCategoryId, targetCategoryId, targetName } = moveOrAddDialog;
        setMoveOrAddDialog(null);
        try {
            // Always assign to target
            await categoriesApi.assignProducts(targetCategoryId, productUuids);
            // If "move", also remove from source
            if (action === 'move') {
                await categoriesApi.removeProducts(sourceCategoryId, productUuids);
            }
            await loadTree();
            if (selectedCategory) {
                if (selectedCategory._id === targetCategoryId || selectedCategory._id === sourceCategoryId) {
                    await refreshCategoryProducts(selectedCategory._id);
                }
            }
            const actionLabel = action === 'move' ? 'moved to' : 'also added to';
            showToast(t('CATEGORIES.TOAST_PRODUCT_MOVED', `Product ${actionLabel} "{{name}}"`, { name: targetName }));
        } catch (err) {
            console.error('Failed to move/add product:', err);
            showToast(t('CATEGORIES.TOAST_ERROR', 'Something went wrong'), 'error');
        }
    };

    const handleAddChild = (parentId: string) => {
        setCreateParentId(parentId);
        setNewCategoryName('');
        setIsCreating(true);
    };

    const handleStartEdit = () => {
        if (!selectedCategory) return;
        const name = typeof selectedCategory.name === 'string'
            ? selectedCategory.name
            : (selectedCategory.name?.en || Object.values(selectedCategory.name)[0] || '');
        setEditName(name);
        setIsEditing(true);
    };

    const selectedDisplayName = selectedCategory
        ? (typeof selectedCategory.name === 'string' ? selectedCategory.name : (selectedCategory.name?.en || ''))
        : '';

    // Products to display in right panel
    const displayProducts = isAllProductsSelected ? allProductsData : categoryProducts;
    const displayLoading = isAllProductsSelected ? allProductsLoading && allProductsPage === 1 : loadingProducts;

    // Group products: direct (in this category) vs subcategory when viewing a non-leaf category
    const groupedProducts = useMemo(() => {
        if (isAllProductsSelected || !selectedCategory || !selectedCategory.children?.length) return null;
        const catId = selectedCategory._id;
        const direct: Product[] = [];
        const bySubcategory = new Map<string, { id: string; name: string; products: Product[] }>();

        // Build a map: descendant category ID → nearest child ID & name
        const childMap = new Map<string, { id: string; name: string }>(); // descendantId → child info
        const mapDescendants = (node: CategoryNode, rootChild: { id: string; name: string }) => {
            childMap.set(node._id, rootChild);
            for (const c of node.children || []) mapDescendants(c, rootChild);
        };
        for (const child of selectedCategory.children) {
            const childName = typeof child.name === 'string' ? child.name : (child.name?.en || Object.values(child.name)[0] || '');
            const childInfo = { id: child._id, name: childName };
            mapDescendants(child, childInfo);
        }

        for (const product of categoryProducts) {
            const prodCatIds: string[] = (product as any).categoryIds || [];
            // Direct if this category's ID is in the product's categoryIds
            if (prodCatIds.includes(catId)) {
                direct.push(product);
            } else {
                // Find which child subtree this product belongs to
                let placed = false;
                for (const pcId of prodCatIds) {
                    const childInfo = childMap.get(pcId);
                    if (childInfo) {
                        if (!bySubcategory.has(childInfo.id)) bySubcategory.set(childInfo.id, { id: childInfo.id, name: childInfo.name, products: [] });
                        bySubcategory.get(childInfo.id)!.products.push(product);
                        placed = true;
                        break;
                    }
                }
                if (!placed) direct.push(product);
            }
        }

        return { direct, subcategories: Array.from(bySubcategory.values()) };
    }, [categoryProducts, selectedCategory, isAllProductsSelected]);

    const allSelected = displayProducts.length > 0 && selectedUuids.size === displayProducts.length;
    const someSelected = selectedUuids.size > 0 && selectedUuids.size < displayProducts.length;

    return (
        <PageContainer
            title={t('CATEGORIES.TITLE', 'Product Categories')}
            breadcrumbs={[
                { label: t('SIDEBAR.PRODUCTS.TITLE', 'Products') },
                { label: t('SIDEBAR.PRODUCTS.CATALOG', 'Catalog'), icon: <LayoutGrid className="size-5" />, href: '/products/catalog' },
                { label: t('SIDEBAR.PRODUCTS.CATEGORIES', 'Categories'), icon: <FolderTree className="size-5" /> }
            ]}
        >
            {/* Family Tabs */}
            {families.length > 1 && (
                <Tabs value={activeFamilySlug} onValueChange={setActiveFamilySlug} className="mb-4">
                    <TabsList className="h-9 flex-wrap">
                        {families.map((f: ProductFamily) => (
                            <TabsTrigger key={f._id} value={f.slug} className="px-3 text-sm">
                                {resolveName(f.name)}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            )}

            <div className="flex h-[calc(100vh-180px)] min-h-0 gap-5 overflow-hidden">

                {/* ── Left Panel: Category Tree ── */}
                <div
                    style={{ width: leftPanelWidth, minWidth: leftPanelWidth }}
                    className="flex flex-col overflow-hidden border border-border bg-card rounded-xl"
                >
                    <div className={cn(
                        'flex items-center gap-2 border-b border-border px-4 transition-[padding]',
                        isCreating ? 'py-2.5' : 'py-3.5',
                    )}>
                        {isCreating ? (
                            <>
                                <Input
                                    className="h-8 flex-1"
                                    placeholder={createParentId ? t('CATEGORIES.ADD_CHILD', 'Subcategory name...') : t('CATEGORIES.NAME_PLACEHOLDER', 'Category name...')}
                                    value={newCategoryName}
                                    onChange={e => setNewCategoryName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setIsCreating(false); }}
                                    autoFocus
                                />
                                <Button variant="ghost" size="icon-sm" aria-label={t('COMMON.CANCEL') as string} onClick={() => setIsCreating(false)}>
                                    <X className="text-muted-foreground" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleCreate} disabled={!newCategoryName.trim()}>
                                    {t('COMMON.CREATE', 'Create')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <h2 className="flex-1 font-heading text-sm font-medium tracking-tight text-foreground">
                                    {t('CATEGORIES.TREE_TITLE', 'Categories')}
                                </h2>
                                {!showRemoveAllConfirm && (
                                    <Button variant="outline" size="sm" onClick={handleAiCategorize} disabled={aiStarting || categorising || !activeFamilySlug}>
                                        {aiStarting || categorising ? <Loader2 className="animate-spin" /> : <Sparkles />}
                                        {t('CATEGORIES.AI_GENERATE', 'Generate with AI')}
                                    </Button>
                                )}
                                {tree.length > 0 && !showRemoveAllConfirm && (
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        className="hover:bg-destructive/5 hover:text-destructive"
                                        onClick={() => setShowRemoveAllConfirm(true)}
                                        title="Remove all categories"
                                    >
                                        <Trash2 className="text-muted-foreground" />
                                    </Button>
                                )}
                                {!showRemoveAllConfirm && (
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() => { setCreateParentId(undefined); setNewCategoryName(''); setIsCreating(true); }}
                                        title="Add root category"
                                    >
                                        <Plus className="text-muted-foreground" />
                                    </Button>
                                )}
                                {showRemoveAllConfirm && (
                                    <div className="flex animate-in items-center gap-1.5 fade-in duration-200">
                                        <span className="whitespace-nowrap text-xs text-muted-foreground">
                                            {t('CATEGORIES.DELETE_ALL_CONFIRM', 'Delete all?')}
                                        </span>
                                        <Button variant="ghost" size="xs" onClick={() => setShowRemoveAllConfirm(false)}>
                                            {t('COMMON.CANCEL', 'Cancel')}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="xs"
                                            className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                                            onClick={handleRemoveAll}
                                        >
                                            {t('COMMON.DELETE', 'Delete')}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex-1 overflow-auto p-1.5">
                        {loading ? (
                            <div className="flex justify-center py-16">
                                <Loader2 className="size-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : tree.length === 0 ? (
                            <div className="flex flex-col items-center justify-center px-6 py-16">
                                <FolderPlus className="size-9 text-muted-foreground/50" />
                                <p className="mt-3 text-center text-sm leading-relaxed text-muted-foreground">
                                    {t('CATEGORIES.NO_CATEGORIES', 'No categories yet. Create your first category to organize products.')}
                                </p>
                                <Button className="mt-4" onClick={() => { setCreateParentId(undefined); setNewCategoryName(''); setIsCreating(true); }}>
                                    {t('CATEGORIES.ADD_ROOT', 'Create Category')}
                                </Button>
                            </div>
                        ) : (
                            <>
                                {/* "All Products" row */}
                                <div
                                    onClick={() => setSelectedCategory(allProductsNode)}
                                    className={cn(
                                        'mb-0.5 flex cursor-pointer items-center gap-1.5 px-2.5 py-1.5 transition-colors',
                                        isAllProductsSelected ? 'bg-muted' : 'hover:bg-muted/60',
                                    )}
                                >
                                    <span className="flex-1 truncate text-sm font-medium text-foreground">
                                        {t('CATEGORIES.ALL_PRODUCTS', 'All Products')}
                                    </span>
                                </div>
                                {tree.map(node => (
                                    <CategoryTreeItem
                                        key={node._id} node={node}
                                        selectedId={selectedCategory?._id || null}
                                        onSelect={setSelectedCategory}
                                        onAddChild={handleAddChild}
                                        onDelete={handleDirectDelete}
                                        onDropProduct={handleDropProduct}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>

                {/* ── Resize Handle ── */}
                <div
                    onMouseDown={handleResizeStart}
                    className="group/resize -mx-1.5 flex w-1.5 shrink-0 cursor-col-resize items-center justify-center"
                >
                    <div className="h-8 w-0.5 bg-border transition-colors group-hover/resize:bg-muted-foreground group-active/resize:bg-muted-foreground" />
                </div>

                {/* ── Right Panel: Category Detail ── */}
                <div className="flex h-full flex-1 flex-col overflow-hidden border border-border bg-card rounded-xl">
                    {selectedCategory ? (
                        <>
                            {/* Detail header */}
                            <div className="flex items-center gap-3 border-b border-border px-5 py-3.5">
                                {isEditing && !isAllProductsSelected ? (
                                    <div className="flex flex-1 items-center gap-2">
                                        <Input className="h-8 flex-1" value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleUpdate()} autoFocus />
                                        <Button onClick={handleUpdate}>{t('COMMON.SAVE', 'Save')}</Button>
                                        <Button variant="outline" onClick={() => setIsEditing(false)}>{t('COMMON.CANCEL', 'Cancel')}</Button>
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="flex-1 truncate font-heading text-base font-medium tracking-tight text-foreground">
                                            {selectedDisplayName}
                                        </h2>
                                        {!isAllProductsSelected && selectedCategory.productCount > 0 && (
                                            <Badge variant="secondary">
                                                {selectedCategory.productCount} {selectedCategory.productCount === 1 ? 'product' : 'products'}
                                            </Badge>
                                        )}
                                        {!isAllProductsSelected && (
                                            <Button variant="outline" size="sm" onClick={() => setCoverDialogOpen(true)}>
                                                {selectedCategory.image && (
                                                    <img src={selectedCategory.image} alt="" className="size-4 object-cover" />
                                                )}
                                                {selectedCategory.image ? t('CATEGORIES.COVER', 'Cover') : t('CATEGORIES.ADD_COVER', 'Add cover')}
                                            </Button>
                                        )}
                                        {!isAllProductsSelected && (
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                onClick={handleStartEdit}
                                                aria-label={t('CATEGORIES.EDIT') as string}
                                            >
                                                <Pencil className="text-muted-foreground" />
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>

                            {/*
                             * One toolbar serves both the category view and "All Products";
                             * only the label and the trailing control differ.
                             */}
                            <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-5 py-2.5">
                                <Checkbox
                                    checked={someSelected ? 'indeterminate' : allSelected}
                                    onCheckedChange={toggleSelectAll}
                                />
                                <span className={cn(
                                    'flex-1 truncate text-xs font-medium tracking-wide text-muted-foreground',
                                    isAllProductsSelected && 'uppercase',
                                )}>
                                    {selectedUuids.size > 0
                                        ? t('CATEGORIES.N_SELECTED', '{{count}} selected', { count: selectedUuids.size })
                                        : isAllProductsSelected
                                            ? t('CATEGORIES.ALL_PRODUCTS', 'All Products')
                                            : t('CATEGORIES.PRODUCTS_IN_CATEGORY', 'Products')
                                    }
                                </span>
                                {selectedUuids.size > 0 ? (
                                    <Button size="sm" onClick={() => setShowBulkAssignPicker(true)}>
                                        {t('CATEGORIES.ASSIGN_TO', 'Assign to category')}
                                    </Button>
                                ) : isAllProductsSelected ? (
                                    <span className="text-xs text-muted-foreground">
                                        {t('CATEGORIES.DRAG_HINT', 'Drag products to a category on the left')}
                                    </span>
                                ) : (
                                    <Button variant="outline" size="sm" onClick={() => setShowProductPicker(true)}>
                                        <Plus />
                                        {t('CATEGORIES.ADD_PRODUCTS', 'Add')}
                                    </Button>
                                )}
                            </div>

                            {/* Products list */}
                            <div className="flex-1 overflow-auto">
                                {displayLoading ? (
                                    <div className="flex justify-center py-16">
                                        <Loader2 className="size-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : displayProducts.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center px-6 py-16">
                                        <Package className="size-10 text-muted-foreground/50" />
                                        <p className="mt-3 text-center text-sm text-muted-foreground">
                                            {isAllProductsSelected
                                                ? t('CATEGORIES.NO_PRODUCTS', 'No products found')
                                                : t('CATEGORIES.NO_PRODUCTS_IN_CATEGORY', 'No products in this category yet')
                                            }
                                        </p>
                                        {!isAllProductsSelected && (
                                            <Button variant="outline" className="mt-3" onClick={() => setShowProductPicker(true)}>
                                                <Plus />
                                                {t('CATEGORIES.ADD_PRODUCTS', 'Add Products')}
                                            </Button>
                                        )}
                                    </div>
                                ) : groupedProducts && !isAllProductsSelected ? (
                                    <div className="flex flex-col gap-3 p-1.5">
                                        {/* Direct products section - entire section is a drop zone */}
                                        <DroppableSection
                                            label={selectedDisplayName || t('CATEGORIES.DIRECT', 'Direct')}
                                            count={groupedProducts.direct.length}
                                            categoryId={selectedCategory._id}
                                            onDropProduct={handleDropProduct}
                                        >
                                            {groupedProducts.direct.map((product: any) => (
                                                <DraggableProductCard
                                                    key={product.uuid}
                                                    product={product}
                                                    onRemove={() => handleRemoveProduct(product.uuid)}
                                                    showRemove={selectedUuids.size === 0}
                                                    sourceCategoryId={selectedCategory?._id}
                                                    isSelected={selectedUuids.has(product.uuid)}
                                                    onToggleSelect={() => toggleSelectUuid(product.uuid)}
                                                    allSelectedUuids={selectedUuids}
                                                />
                                            ))}
                                        </DroppableSection>
                                        {/* Subcategory groups - entire section is a drop zone */}
                                        {groupedProducts.subcategories.map(group => (
                                            <DroppableSection
                                                key={group.id}
                                                label={group.name}
                                                count={group.products.length}
                                                categoryId={group.id}
                                                onDropProduct={handleDropProduct}
                                            >
                                                {group.products.map((product: any) => (
                                                    <DraggableProductCard
                                                        key={product.uuid}
                                                        product={product}
                                                        showRemove={false}
                                                        sourceCategoryId={group.id}
                                                        isSelected={selectedUuids.has(product.uuid)}
                                                        onToggleSelect={() => toggleSelectUuid(product.uuid)}
                                                        allSelectedUuids={selectedUuids}
                                                    />
                                                ))}
                                            </DroppableSection>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-[repeat(auto-fill,minmax(90px,1fr))] gap-2 p-1.5">
                                        {displayProducts.map((product: any) => (
                                            <DraggableProductCard
                                                key={product.uuid}
                                                product={product}
                                                onRemove={!isAllProductsSelected ? () => handleRemoveProduct(product.uuid) : undefined}
                                                showRemove={!isAllProductsSelected && selectedUuids.size === 0}
                                                sourceCategoryId={selectedCategory?._id}
                                                isSelected={selectedUuids.has(product.uuid)}
                                                onToggleSelect={() => toggleSelectUuid(product.uuid)}
                                                allSelectedUuids={selectedUuids}
                                            />
                                        ))}
                                        {/* Load more for "All Products" */}
                                        {isAllProductsSelected && allProductsNextPage > 0 && (
                                            <div className="flex justify-center py-4">
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setAllProductsPage(prev => prev + 1)}
                                                    disabled={allProductsLoading}
                                                >
                                                    {allProductsLoading
                                                        ? <Loader2 className="size-4 animate-spin" />
                                                        : t('CATEGORIES.LOAD_MORE', 'Load more')
                                                    }
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-1 items-center justify-center">
                            <div className="flex flex-col items-center">
                                <FolderTree className="size-20 text-foreground opacity-15" />
                                <p className="mt-6 text-sm font-medium text-muted-foreground">
                                    {t('CATEGORIES.SELECT_CATEGORY_TITLE', 'No category selected')}
                                </p>
                                <p className="mt-1.5 text-sm text-muted-foreground/70">
                                    {t('CATEGORIES.SELECT_CATEGORY_SUBTITLE', 'Select a category to view its details')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── AI Agent Right Panel ── */}
            </div>

            {/* Remove All Confirm Dialog removed — now inline in left panel header */}

            {/* ── AI Confirm Dialog ── */}
            <Dialog open={showAiConfirm} onOpenChange={next => { if (!next) setShowAiConfirm(false); }}>
                <DialogContent className="gap-0 p-0">
                    <DialogHeader className="border-b border-border px-5 py-4">
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="size-4" />
                            {t('CATEGORIES.AI_GENERATE', 'Generate with AI')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="px-5 py-5">
                        <p className="mb-4 text-xs text-muted-foreground">
                            {t('CATEGORIES.AI_MODE_DESC', 'Choose how the AI agent should organize your products:')}
                        </p>
                        <div className="flex flex-col gap-3">
                            <div
                                onClick={() => setAiMode('generate')}
                                className={cn(
                                    'cursor-pointer rounded-lg border p-4 transition-colors',
                                    aiMode === 'generate' ? 'border-foreground bg-muted' : 'border-border hover:border-muted-foreground',
                                )}
                            >
                                <p className="mb-1 text-sm font-medium text-foreground">
                                    {t('CATEGORIES.AI_MODE_GENERATE', 'Create new categories')}
                                </p>
                                <p className="text-xs leading-relaxed text-muted-foreground">
                                    {t('CATEGORIES.AI_MODE_GENERATE_DESC', 'AI will analyze your products and create a new category structure, then assign products to the appropriate categories.')}
                                </p>
                            </div>
                            <div
                                onClick={() => setAiMode('assign')}
                                className={cn(
                                    'cursor-pointer rounded-lg border p-4 transition-colors',
                                    aiMode === 'assign' ? 'border-foreground bg-muted' : 'border-border hover:border-muted-foreground',
                                    tree.length === 0 && 'pointer-events-none opacity-50',
                                )}
                            >
                                <p className="mb-1 text-sm font-medium text-foreground">
                                    {t('CATEGORIES.AI_MODE_ASSIGN', 'Use existing categories')}
                                </p>
                                <p className="text-xs leading-relaxed text-muted-foreground">
                                    {t('CATEGORIES.AI_MODE_ASSIGN_DESC', 'AI will assign products to your existing categories without creating new ones.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="mx-0 mb-0 border-t border-border px-5 py-4">
                        <Button variant="outline" onClick={() => setShowAiConfirm(false)}>{t('COMMON.CANCEL', 'Cancel')}</Button>
                        <Button onClick={handleAiRun}>{t('CATEGORIES.AI_RUN', 'Start')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Product Picker Dialog ── */}
            <ProductPickerDialog
                open={showProductPicker}
                onClose={() => setShowProductPicker(false)}
                onConfirm={handleAssignProducts}
                familySlug={activeFamilySlug || undefined}
                existingUuids={categoryProducts.map((p: any) => p.uuid)}
            />

            {/* ── Move or Add Dialog ── */}
            <Dialog open={!!moveOrAddDialog} onOpenChange={next => { if (!next) setMoveOrAddDialog(null); }}>
                <DialogContent className="gap-0 p-0">
                    <DialogHeader className="border-b border-border px-5 py-4">
                        <DialogTitle>{t('CATEGORIES.MOVE_OR_ADD', 'Move or Add?')}</DialogTitle>
                    </DialogHeader>
                    <p className="px-5 py-5 text-sm leading-relaxed text-muted-foreground">
                        {t('CATEGORIES.MOVE_OR_ADD_DESC', 'This product is already in another category. Would you like to move it (remove from current category) or add it to both?')}
                    </p>
                    <DialogFooter className="mx-0 mb-0 border-t border-border px-5 py-4">
                        <Button variant="outline" onClick={() => setMoveOrAddDialog(null)}>{t('COMMON.CANCEL', 'Cancel')}</Button>
                        <Button variant="outline" onClick={() => handleMoveOrAddConfirm('add')}>
                            {t('CATEGORIES.ADD_TO_BOTH', 'Add to both')}
                        </Button>
                        <Button onClick={() => handleMoveOrAddConfirm('move')}>
                            {t('CATEGORIES.MOVE', 'Move')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Bulk Assign Category Picker ── */}
            <Dialog open={showBulkAssignPicker} onOpenChange={next => { if (!next) setShowBulkAssignPicker(false); }}>
                <DialogContent className="gap-0 p-0">
                    <DialogHeader className="border-b border-border px-5 py-4">
                        <DialogTitle className="pe-6">
                            {t('CATEGORIES.ASSIGN_TO_CATEGORY', 'Assign {{count}} product(s) to category', { count: selectedUuids.size })}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[400px] overflow-auto p-1.5">
                        {tree.length === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                {t('CATEGORIES.NO_CATEGORIES', 'No categories yet')}
                            </p>
                        ) : (
                            <BulkAssignTree nodes={tree} onSelect={handleBulkAssign} />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Category cover dialog ── */}
            <Dialog open={coverDialogOpen} onOpenChange={next => { if (!next && !coverBusy) setCoverDialogOpen(false); }}>
                <DialogContent className="gap-0 p-0">
                    <DialogHeader className="border-b border-border px-5 py-4">
                        <DialogTitle>{t('CATEGORIES.COVER_TITLE', 'Category cover')}</DialogTitle>
                    </DialogHeader>
                    <div className="px-5 py-5">
                        {/* Preview */}
                        <div className="relative mb-3 flex aspect-square w-full items-center justify-center overflow-hidden border border-border bg-muted">
                            {selectedCategory?.image ? (
                                <img src={selectedCategory.image} alt="" className="size-full object-cover" />
                            ) : (
                                <p className="text-sm text-muted-foreground">{t('CATEGORIES.NO_COVER', 'No cover yet')}</p>
                            )}
                            {coverBusy && (
                                <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                                    <Loader2 className="size-7 animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </div>
                        {selectedCategory?.image && (
                            <p className="mb-4 text-center text-xs text-muted-foreground">
                                {selectedCategory.coverIsCustom
                                    ? t('CATEGORIES.COVER_CUSTOM', 'Custom image')
                                    : t('CATEGORIES.COVER_AUTO', 'Auto-generated from products')}
                            </p>
                        )}

                        <input ref={coverFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleUploadCover(e.target.files?.[0])} />

                        <div className="flex flex-col gap-2">
                            <Button className="w-full" disabled={coverBusy} onClick={handleGenerateCover}>
                                {t('CATEGORIES.COVER_GENERATE', 'Generate from products')}
                            </Button>
                            <Button variant="outline" className="w-full" disabled={coverBusy} onClick={() => coverFileInputRef.current?.click()}>
                                {t('CATEGORIES.COVER_UPLOAD', 'Upload custom image')}
                            </Button>
                            {selectedCategory?.image && (
                                <Button
                                    variant="outline"
                                    className="w-full border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                    disabled={coverBusy}
                                    onClick={handleRemoveCover}
                                >
                                    {t('CATEGORIES.COVER_REMOVE', 'Remove cover')}
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>


            {/* ── Toast ── */}
            <Toast
                message={toast?.message || ''}
                type={toast?.type || 'success'}
                isVisible={!!toast}
                onClose={() => setToast(null)}
            />
        </PageContainer>
    );
};

export default CategoriesPage;
